import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import {
  CalendarClock,
  ImageIcon,
  Loader2,
  Megaphone,
  Minus,
  Plus,
  Sparkles,
  X,
} from "lucide-react";
import {
  STANDALONE_ASSET_BATCH_MAX_ITEMS,
  type StandaloneAssetBatchCreateRequestV1,
} from "@runcheetah/shared";

import {
  STANDALONE_ASSET_FORMATS,
  buildStandaloneAssetCreateRequest,
  createEmptyStandaloneAssetCounts,
  getStandaloneAssetDefaultFirstScheduledFor,
  getStandaloneAssetCountTotal,
  type StandaloneAssetPostType,
  updateStandaloneAssetCount,
} from "@/lib/standalone-assets";
import { trpc } from "@/lib/trpc";
import { buildBrandPath } from "@/hooks/use-route-brand";
import { RunConfirmationDialog } from "@/components/runs/run-confirmation-dialog";
import { Button, Card, CardContent, Input, Textarea } from "@runcheetah/ui";

const fallbackQuoteRequest: StandaloneAssetBatchCreateRequestV1 = {
  version: 1,
  prompt: "Run quote",
  audienceOverride: null,
  items: [
    {
      scheduledFor: new Date(0).toISOString(),
      platform: "instagram",
      postType: "feed_portrait",
      brandPresence: "branded",
    },
  ],
};

export function CreateAssetBatchModal({
  brandId,
  open,
  onOpenChange,
  onCreateCampaign,
  onUpgradeRequired,
  upgradeLabel,
  initialFirstScheduledFor,
}: {
  brandId: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateCampaign?: () => void;
  onUpgradeRequired?: () => void;
  upgradeLabel?: string;
  initialFirstScheduledFor?: string;
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [prompt, setPrompt] = useState("");
  const [counts, setCounts] = useState(createEmptyStandaloneAssetCounts);
  const [firstScheduledFor, setFirstScheduledFor] = useState(
    getStandaloneAssetDefaultFirstScheduledFor,
  );
  const [pendingRequest, setPendingRequest] = useState<StandaloneAssetBatchCreateRequestV1 | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const createBatch = trpc.standaloneAssetBatch.create.useMutation({
    onSuccess: async () => {
      if (brandId) {
        await utils.standaloneAssetBatch.list.invalidate({ brandId });
        await utils.brand.getBillingOverview.invalidate({ brandId });
      }
      onOpenChange(false);
      setPendingRequest(null);
      if (brandId) {
        navigate(buildBrandPath(brandId, "/review"));
      }
    },
    onError: (mutationError) => setError(mutationError.message),
  });
  const runQuoteQuery = trpc.standaloneAssetBatch.quoteCreate.useQuery(
    {
      brandId: brandId ?? "00000000-0000-0000-0000-000000000000",
      request: pendingRequest ?? fallbackQuoteRequest,
    },
    { enabled: Boolean(brandId && pendingRequest), retry: false },
  );
  const total = getStandaloneAssetCountTotal(counts);
  const canSubmit = Boolean(brandId && prompt.trim() && total > 0 && firstScheduledFor);

  useEffect(() => {
    if (!open) {
      return;
    }

    setFirstScheduledFor(initialFirstScheduledFor ?? getStandaloneAssetDefaultFirstScheduledFor());
  }, [initialFirstScheduledFor, open]);

  if (!open) {
    return null;
  }

  const setCount = (postType: StandaloneAssetPostType, delta: number) => {
    setCounts((current) =>
      updateStandaloneAssetCount({
        counts: current,
        postType,
        delta,
        maxTotal: STANDALONE_ASSET_BATCH_MAX_ITEMS,
      }),
    );
  };

  const submit = () => {
    setError(null);
    if (!brandId) {
      setError(
        intl.formatMessage({
          id: "createAssetBatchModal.loadBrandError",
          defaultMessage: "Load your brand before creating assets.",
        }),
      );
      return;
    }

    setPendingRequest(
      buildStandaloneAssetCreateRequest({
        prompt,
        counts,
        firstScheduledFor,
        intervalHours: 24,
      }),
    );
  };

  const confirmSubmit = () => {
    if (!brandId || !pendingRequest) {
      return;
    }

    createBatch.mutate({ brandId, request: pendingRequest });
  };

  const createCampaignInstead = () => {
    onOpenChange(false);
    onCreateCampaign?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 p-3 backdrop-blur-sm sm:p-4">
      <Card className="max-h-[calc(100dvh-1.5rem)] w-full max-w-2xl overflow-y-auto rounded-3xl shadow-xl sm:max-h-[calc(100dvh-2rem)]">
        <CardContent className="space-y-6 p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                <FormattedMessage id="createAssetBatchModal.badge" defaultMessage="Create assets" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold tracking-tight">
                  <FormattedMessage
                    id="createAssetBatchModal.title"
                    defaultMessage="Standalone asset batch"
                  />
                </h2>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">
                  <FormattedMessage
                    id="createAssetBatchModal.description"
                    defaultMessage="Brief Cheetah once, pick supported Instagram formats, and set the first planned slot."
                  />
                </p>
              </div>
            </div>
            <Button type="button" size="icon" variant="ghost" onClick={() => onOpenChange(false)}>
              <X className="h-5 w-5" aria-hidden="true" />
              <span className="sr-only">
                <FormattedMessage id="common.close" defaultMessage="Close" />
              </span>
            </Button>
          </div>

          <div className="space-y-3">
            <label className="text-sm font-medium" htmlFor="standalone-asset-prompt">
              <FormattedMessage
                id="createAssetBatchModal.promptLabel"
                defaultMessage="What should these posts be about?"
              />
            </label>
            <Textarea
              id="standalone-asset-prompt"
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              rows={5}
              placeholder={intl.formatMessage({
                id: "createAssetBatchModal.promptPlaceholder",
                defaultMessage:
                  "Create a warm batch of standalone posts that make our product feel practical and personal.",
              })}
            />
          </div>

          {onCreateCampaign ? (
            <button
              type="button"
              onClick={createCampaignInstead}
              className="flex w-full items-start gap-3 rounded-2xl border border-accent/20 bg-accent/5 px-4 py-3 text-left transition-colors hover:bg-accent/10"
            >
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent">
                <Megaphone className="h-4 w-4" aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-foreground">
                  <FormattedMessage
                    id="createAssetBatchModal.campaignAlternativeTitle"
                    defaultMessage="Need coordinated posts?"
                  />
                </span>
                <span className="mt-1 block text-sm leading-5 text-muted-foreground">
                  <FormattedMessage
                    id="createAssetBatchModal.campaignAlternativeDescription"
                    defaultMessage="Create a campaign instead to generate a strategic multi-post plan."
                  />
                </span>
              </span>
            </button>
          ) : null}

          <div className="rounded-2xl border">
            <div className="grid grid-cols-[1fr_auto] border-b px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <span>
                <FormattedMessage id="common.format" defaultMessage="Format" />
              </span>
              <span>
                <FormattedMessage id="common.count" defaultMessage="Count" />
              </span>
            </div>
            {STANDALONE_ASSET_FORMATS.map((format) => (
              <div
                key={format.postType}
                className="grid grid-cols-[1fr_auto] items-center gap-4 border-b px-4 py-4 last:border-b-0"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted">
                    <ImageIcon className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="font-medium">{intl.formatMessage(format.label)}</div>
                    <div className="text-sm text-muted-foreground">
                      {intl.formatMessage(format.description)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    onClick={() => setCount(format.postType, -1)}
                    disabled={counts[format.postType] === 0}
                  >
                    <Minus className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  <div className="w-10 text-center font-semibold">{counts[format.postType]}</div>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    onClick={() => setCount(format.postType, 1)}
                    disabled={total >= STANDALONE_ASSET_BATCH_MAX_ITEMS}
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="standalone-asset-schedule">
                <FormattedMessage
                  id="createAssetBatchModal.firstPlannedSlot"
                  defaultMessage="First planned slot"
                />
              </label>
              <Input
                id="standalone-asset-schedule"
                type="datetime-local"
                value={firstScheduledFor}
                onChange={(event) => setFirstScheduledFor(event.target.value)}
              />
            </div>
            <div className="flex items-center gap-2 rounded-2xl border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
              <CalendarClock className="h-4 w-4" aria-hidden="true" />
              <FormattedMessage
                id="createAssetBatchModal.dailySpacing"
                defaultMessage="Subsequent assets are spaced daily."
              />
            </div>
          </div>

          {error ? (
            <div className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          ) : null}

          <div className="flex flex-col gap-3 border-t pt-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              <FormattedMessage
                id="createAssetBatchModal.selectedCount"
                defaultMessage="{total} of {max} assets selected"
                values={{ total, max: STANDALONE_ASSET_BATCH_MAX_ITEMS }}
              />
            </p>
            <Button type="button" onClick={submit} disabled={!canSubmit || createBatch.isPending}>
              {createBatch.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : null}
              <FormattedMessage
                id="createAssetBatchModal.generateBatch"
                defaultMessage="Generate batch"
              />
            </Button>
          </div>

          <RunConfirmationDialog
            open={Boolean(pendingRequest)}
            title={intl.formatMessage({
              id: "createAssetBatchModal.runConfirmTitle",
              defaultMessage: "Use runs to generate this batch?",
            })}
            description={intl.formatMessage({
              id: "createAssetBatchModal.runConfirmDescription",
              defaultMessage: "Cheetah will start generating the selected standalone posts.",
            })}
            confirmLabel={intl.formatMessage({
              id: "createAssetBatchModal.runConfirmAction",
              defaultMessage: "Generate",
            })}
            upgradeLabel={upgradeLabel}
            quote={runQuoteQuery.data ?? null}
            quotePending={runQuoteQuery.isPending || runQuoteQuery.isFetching}
            confirmPending={createBatch.isPending}
            error={error ?? runQuoteQuery.error?.message ?? null}
            onCancel={() => {
              if (!createBatch.isPending) {
                setPendingRequest(null);
              }
            }}
            onUpgrade={onUpgradeRequired}
            onConfirm={confirmSubmit}
          />
        </CardContent>
      </Card>
    </div>
  );
}
